import { useMsal } from '@azure/msal-react'
import { Box, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { updateUserLanguage } from '../api/services/apiService'
import { useCurrentSite } from '../store/store.hooks'
import { useUserDetails } from '../user/hooks/user.hooks'
import { logger } from '../utils/logger'
import AppTopBar from './AppBar/AppTopBar'
import AppVersionSnackbar from './AppVersionSnackbar'
import Sidebar, { MINI_DRAWER_WIDTH } from './SideBar/Sidebar'

const drawerWidth = 275

const MainLayout = () => {

  const theme = useTheme()
  const isMediumDown = useMediaQuery(theme.breakpoints.down('md'))
  const currentSite = useCurrentSite()

  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { instance, accounts } = useMsal()
  const userDetails = useUserDetails()

  const location = useLocation()
  const hideSidebar = location.pathname.startsWith('/adminReports')

  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopOpen, setDesktopOpen] = useState(true)

  const handleMenuToggle = () => {
    if (isMediumDown) {
      setMobileOpen((prev) => !prev)
    } else {
      setDesktopOpen((prev) => !prev)
    }
  }

  useEffect(() => {
    if (
      !isMediumDown &&
      (location.pathname.startsWith('/WeSaw') ||
        location.pathname.startsWith('/WePlane') ||
        location.pathname.startsWith('/quality-control'))
    ) {
      setDesktopOpen(false)
    }
  }, [location.pathname, isMediumDown])

  // états version app
  const [refreshAlert, setRefreshAlert] = useState(false)
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(120)

  useEffect(() => {
    const checkForNewAppVersion = async () => {
      try {
        const host = window.location.origin
        const response = await fetch(`${host}`)
        const text = await response.text()
        const remoteScripts = Array.from(text.matchAll(/<script.*?src="(.*?\.chunk\.js)".*?>/g))
          .map((match) => match[1])
          .map((script) => script.split('/').pop())
          .slice(-2)
        if (remoteScripts.length < 2) return
        const localScripts = Array.from(document.getElementsByTagName('script'))
          .map((script) => script.src.split('/').pop())
          .filter(Boolean)
          .slice(-2)
        if (localScripts.length < 2) return
        const scriptsDiffer = remoteScripts.some((remoteScript, index) => remoteScript !== localScripts[index])
        if (scriptsDiffer) {
          setRefreshAlert(true)
          const intervalId = setInterval(() => {
            setSecondsUntilRefresh((prev) => {
              if (prev > 1) {
                return prev - 1
              } else {
                clearInterval(intervalId)
                window.location.reload()
                return 0
              }
            })
          }, 1000)
        }
      } catch (err) {
        logger.error('Error checking for new app version:', err)
      }
    }
    checkForNewAppVersion()
    const intervalId = setInterval(checkForNewAppVersion, 180000)
    return () => clearInterval(intervalId)
  }, [])

  const handleRefreshNow = () => window.location.reload()

  const handleLogout = () => {
    instance.logoutRedirect({ account: accounts[0] })
  }

  const menuItems = [
    {
      title: t('otherLanguage'),
      async action() {
        const newLanguage = i18n.language === 'fr' ? 'en' : 'fr'
        i18n.changeLanguage(newLanguage)
        const res = await updateUserLanguage(newLanguage)
        logger.log('[MainLayout] language saved to DB, status:', res.status)
      },
    },
    { title: t('userSettings'), action: () => navigate('/UserSettings') },
    ...(userDetails?.isAdmin ? [{ title: t('admin'), action: () => navigate('/admin') }] : []),
    { title: t('logout'), action: handleLogout },
  ]

  return (
    <>
      <AppTopBar
        onMenuClick={handleMenuToggle}
        onSiteClick={() => { isMediumDown ? setMobileOpen(true) : setDesktopOpen(true) }}
        isSmallDown={isMediumDown}
        showHamburger={!!currentSite && !hideSidebar}
        desktopOpen={desktopOpen}
        menuItems={menuItems}
      />
      <Box sx={{ display: 'flex', height: '100%', paddingTop: '64px' }}>
        {currentSite && !hideSidebar && (
          <Sidebar
            isMediumDown={isMediumDown}
            mobileOpen={mobileOpen}
            desktopOpen={desktopOpen}
            onClose={handleMenuToggle}
            onOpen={() => setDesktopOpen(true)}
            drawerWidth={drawerWidth}
          />
        )}
        <Box
          component="main"
          sx={(t) => ({
            flex: 1,
            transition: t.transitions.create(['margin', 'width'], {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.leavingScreen,
            }),
            marginLeft: isMediumDown || !currentSite || hideSidebar ? 0 : desktopOpen ? `${drawerWidth}px` : `${MINI_DRAWER_WIDTH}px`,
            width: isMediumDown || !currentSite || hideSidebar ? '100%' : desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)`,
          })}
        >
          <Outlet />
        </Box>
      </Box>
      <AppVersionSnackbar
        open={refreshAlert}
        secondsUntilRefresh={secondsUntilRefresh}
        onRefreshNow={handleRefreshNow}
      />
    </>
  )
}
export default MainLayout
